/**
 * LCG KLAVIYO SUBSCRIBE
 *
 * Submits any <form data-lcg-klaviyo> to Klaviyo's public Client Subscriptions API,
 * one request per list, so a single signup can join several lists. Used by the
 * homepage newsletter (Retail) and the unlisted QR join page (Retail + Met at Local
 * Events). The Klaviyo app embed is deliberately NOT enabled on this theme, so this
 * script is the only Klaviyo code the storefront loads.
 *
 * Form contract (all on the <form>):
 *   data-site-id   Klaviyo public Site ID (6 chars). Public by design; never a private key.
 *   data-lists     Comma-separated list IDs, subscribed in this order (see below).
 *   data-source    Recorded as the consent source and as profile property signup_source.
 * Inside the form: input[name=email] (required), optional input[name=first_name],
 * a submit button, a hidden honeypot input[name=lcg_hp], and [data-lcg-status]
 * (aria-live) for errors. A sibling or descendant [data-lcg-done] is revealed on success.
 *
 * Why one request per list, sequentially: the endpoint accepts exactly one list per
 * subscription. Sequential (not parallel) requests avoid two simultaneous "create
 * profile" operations racing for the same new email address.
 *
 * ORDER MATTERS: put single opt-in lists first and the double opt-in list LAST.
 * Observed 2026-11-03 (undocumented by Klaviyo): when one profile is subscribed to two
 * double opt-in lists back to back, Klaviyo sends ONE confirmation email, for the most
 * recent request, and the earlier list never completes. Retail (double opt-in, and the
 * list that triggers the welcome flow with the 10% code) must therefore be the last
 * request. Met at Local Events was switched to single opt-in for the same reason.
 *
 * Success rule: every list must accept the signup. If any request fails, the customer
 * sees the error and can simply resubmit; re-subscribing a list they already joined is
 * harmless. Success copy must say "check your inbox", because Retail is double opt-in.
 *
 * Complexity: O(L) network requests for L lists; L is 1 or 2 here.
 */
(function () {
  'use strict';
  if (window.LCGKlaviyoSubscribe) return; // the script may be included by two sections on one page
  window.LCGKlaviyoSubscribe = true;

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/?company_id=';
  var REVISION = '2025-10-15';
  // Deliberately loose: the browser's type=email check runs first, and Klaviyo
  // validates authoritatively. This only stops obvious typos before a network call.
  var EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** POST one subscription. Resolves on 2xx (Klaviyo answers 202), rejects otherwise. */
  function subscribe(siteId, listId, profile, source) {
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: { data: { type: 'profile', attributes: profile } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    return fetch(ENDPOINT + encodeURIComponent(siteId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('Klaviyo responded ' + res.status + ' for list ' + listId);
    });
  }

  /** Subscribe to every list, strictly in the given order; rejects on the first failure. */
  function subscribeAll(siteId, lists, profile, source) {
    return lists.reduce(function (chain, listId) {
      return chain.then(function () { return subscribe(siteId, listId, profile, source); });
    }, Promise.resolve());
  }

  function bind(form) {
    if (form.dataset.lcgBound) return;
    form.dataset.lcgBound = '1';

    var siteId = form.dataset.siteId;
    var lists = (form.dataset.lists || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var source = form.dataset.source || 'Website';
    var status = form.querySelector('[data-lcg-status]');
    var button = form.querySelector('[type="submit"]');
    var done = (form.parentNode && form.parentNode.querySelector('[data-lcg-done]')) || form.querySelector('[data-lcg-done]');
    var errorText = form.dataset.errorText || 'Something went wrong. Please try again.';

    if (!siteId || lists.length === 0) return; // misconfigured: leave the form inert rather than half-working

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (status) status.textContent = '';

      var emailInput = form.querySelector('input[name="email"]');
      var nameInput = form.querySelector('input[name="first_name"]');
      var honeypot = form.querySelector('input[name="lcg_hp"]');
      var email = emailInput ? emailInput.value.trim() : '';

      if (!EMAIL_SHAPE.test(email)) {
        if (status) status.textContent = 'Please enter a valid email address.';
        if (emailInput) emailInput.focus();
        return;
      }

      // A filled honeypot means a bot. Show success so it learns nothing, send nothing.
      if (honeypot && honeypot.value) { showDone(); return; }

      var profile = { email: email, properties: { signup_source: source } };
      var firstName = nameInput ? nameInput.value.trim() : '';
      if (firstName) profile.first_name = firstName;

      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }

      subscribeAll(siteId, lists, profile, source)
        .then(showDone)
        .catch(function (err) {
          if (window.console) console.error('[lcg-klaviyo]', err.message);
          if (status) status.textContent = errorText;
        })
        .then(function () {
          if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
        });
    });

    function showDone() {
      if (!done) { if (status) status.textContent = 'Thanks! Check your inbox to confirm.'; return; }
      form.hidden = true;
      done.hidden = false;
      done.focus(); // move screen readers and keyboard users to the confirmation
    }
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('form[data-lcg-klaviyo]'), bind);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Theme editor: re-bind when a section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
